import { useState, useEffect } from 'react'
import API from '../services/api.js'

export function useQuestionnaires(assetId) {
  const [questionnaires, setQuestionnaires] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (assetId) {
      fetchQuestionnaires()
    } else {
      setLoading(false)
    }
    // No Django realtime channel exists yet (see rcm hooks' polling
    // approach) — this list simply refetches on refetch()/create/update
    // calls for now, same tradeoff already accepted elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId])

  async function fetchQuestionnaires() {
    setLoading(true)
    try {
      const { data } = await API.get('/questionnaires/', { params: { asset_id: assetId } })
      setQuestionnaires(data || [])
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }

  async function createQuestionnaire(title, description) {
    try {
      const { data } = await API.post('/questionnaires/', {
        title,
        description: description || null,
        asset_id: assetId,
      })
      await fetchQuestionnaires()
      return data
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function updateQuestionnaire(id, updates) {
    try {
      await API.patch(`/questionnaires/${id}/`, { ...updates, asset_id: assetId })
      await fetchQuestionnaires()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  return {
    questionnaires,
    loading,
    error,
    createQuestionnaire,
    updateQuestionnaire,
    refetch: fetchQuestionnaires,
  }
}

export function useQuestionnaireDetail(id, assetId) {
  const [questionnaire, setQuestionnaire] = useState(null)
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!id || id === 'new' || !assetId) {
      setLoading(false)
      return
    }
    fetchQuestionnaire()
    fetchQuestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, assetId])

  async function fetchQuestionnaire() {
    try {
      const { data } = await API.get(`/questionnaires/${id}/`, { params: { asset_id: assetId } })
      setQuestionnaire(data)
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }

  async function fetchQuestions() {
    try {
      const { data } = await API.get(`/questionnaires/${id}/questions/`, { params: { asset_id: assetId } })
      setQuestions(data || [])
    } catch (err) {
      console.error('fetchQuestions failed:', err?.response?.data?.detail || err.message)
    }
  }

  async function updateStatus(newStatus) {
    try {
      await API.patch(`/questionnaires/${id}/`, { status: newStatus, asset_id: assetId })
      setQuestionnaire(prev => ({ ...prev, status: newStatus }))
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function addQuestion(questionData) {
    try {
      await API.post(`/questionnaires/${id}/questions/`, {
        question_text: questionData.question_text,
        question_type: questionData.question_type,
        options: questionData.options || null,
        hint: questionData.hint || null,
        asset_id: assetId,
      })
      await fetchQuestions()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function updateQuestion(questionId, updates) {
    try {
      await API.patch(`/questionnaires/questions/${questionId}/`, { ...updates, asset_id: assetId })
      await fetchQuestions()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function deleteQuestion(questionId) {
    try {
      await API.delete(`/questionnaires/questions/${questionId}/`, { params: { asset_id: assetId } })
      await fetchQuestions()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function reorderQuestions(reorderedQuestions) {
    try {
      await Promise.all(
        reorderedQuestions.map((q, index) =>
          API.patch(`/questionnaires/questions/${q.id}/`, { order_index: index, asset_id: assetId })
        )
      )
      await fetchQuestions()
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function submitAnswer(questionId, answerValue) {
    try {
      const { data } = await API.post('/questionnaires/answers/', {
        questionnaire: id,
        question: questionId,
        answer_value: answerValue,
        asset_id: assetId,
      })
      return data
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function fetchResults() {
    try {
      const { data } = await API.get(`/questionnaires/${id}/results/`, { params: { asset_id: assetId } })
      return data
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  async function reviewMapping(answerId, controlId, mappingStatus, editedReasoning = null) {
    try {
      const { data } = await API.post('/questionnaires/mappings/review/', {
        answer_id: answerId,
        control_id: controlId,
        status: mappingStatus,
        edited_reasoning: editedReasoning,
        asset_id: assetId,
      })
      return data
    } catch (err) {
      throw new Error(err?.response?.data?.detail || err.message)
    }
  }

  return {
    questionnaire,
    questions,
    loading,
    error,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
    updateStatus,
    submitAnswer,
    fetchResults,
    reviewMapping,
    refetch: fetchQuestions,
    
  }
}